import React, { useState } from "react";
import { connect } from "react-redux";
import { Card, Tag, Rate } from "antd";
import { PIPELINE_STEPS } from "variables/constants";
import { getNameInitials } from "utils/index";
import Button  from "components/button";
import IndexModal from "components/modal";
import CandidateScoreCard from "./CandidateScoreCard";

import {
    MapPin,
    Clock,
} from "lucide-react";
import { 
	getApplicantScore
} from "../../../../redux/recruitment/recruitmentState";

const CandidateCard = ({
    recordData,
    role,
    skills = [],
    onClick,
    selected = false,
    stage = "Application",
    code,
    doApplicantScore,
    key,
    authUser,
}) => {
    const StageIcon = PIPELINE_STEPS[stage];  
    const [showScoreDetails, setShowScoreDetails] = useState(false);
    const onClickGetScore = () => {
        doApplicantScore({id: recordData.jap_id});
    }

    const isClient = authUser?.user_level === 4;

    const onClickScoreDetails = (e) => {
        e.stopPropagation();
        setShowScoreDetails(true);
        // doApplicantScore({id: recordData.jap_id, score: recordData?.score});
    }

    return (
        <Card
            key={key}
            hoverable
            onClick={onClick}
            className={`!rounded-2xl !border-2 transition-all duration-300 cursor-pointer ${
                selected
                    ? "!border-emerald-500 !bg-gradient-to-br from-emerald-50 to-cyan-50"
                    : "!border-slate-200 hover:!border-emerald-300"
                }`}
        >
            <div className="flex flex-col space-y-3">
                <div className="flex justify-between items-start">
                    <div className="flex items-start gap-3">
                        <span 
                            className={`relative flex w-12 h-12 shrink-0 overflow-hidden rounded-full border-2 border-white shadow-md  ${
                                selected
                                    ? "bg-gradient-to-br from-emerald-500 to-cyan-500"
                                    : "bg-emerald-100"
                                }`}
                        >
                            <span className={`flex size-full items-center justify-center font-semibold text-[14px] ${
                                selected
                                    ? "text-white"
                                    : "text-emerald-800"
                                }`}
                            >
                                { getNameInitials(recordData?.applicant_name) }
                            </span>
                        </span>

                        <div className="flex-1 min-w-0">
                            <h4 className={`font-semibold text-[13px] sm:text-[15px] text-slate-900`}>
                                {recordData?.applicant_name}
                            </h4>
                            <p className="text-[12px] text-slate-700">{recordData?.job_post_title}</p>

                            <div className="flex items-center gap-3 text-[12px] text-slate-700 mt-1">
                                {recordData?.applicant_location && (
                                    <span className="flex items-center gap-1">
                                        <MapPin className="w-3 h-3 text-gray-400" />
                                        {recordData.applicant_location}
                                    </span>
                                )}
                                {recordData?.applicant?.apl_xp_years && (
                                    <span className="flex items-center gap-1">
                                        <Clock className="w-3 h-3 text-slate-700" />
                                        {recordData.applicant.apl_xp_years} yrs
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="text-right">
                        <div
                            className={`status-indicator normal text-[9px] ${
                                selected ? "bg-gradient-to-br from-emerald-50 to-cyan-50" : ""
                            }`}
                        >
                            { recordData?.job_code }
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                            { recordData?.score > 0 ? 
                                <>
                                <Rate size="small" disabled={true} allowHalf defaultValue={ recordData?.score / 20 } />
                                <Button className="flex items-center justify-center gap-2 border border-gray-300 rounded-lg py-1 text-[12px] text-gray-800 font-medium hover:bg-gray-50 transition"
                                    onClick={onClickScoreDetails}
                                    >
                                    <span>{ recordData?.score }</span>
                                </Button>
                                </>
                            :
                                <Button className="flex items-center justify-center gap-2 border border-gray-300 rounded-lg py-1 text-[12px] text-gray-800 font-medium hover:bg-gray-50 transition"
                                    onClick={onClickGetScore}>
                                    <span>Get Score</span>
                                </Button>
                            }
                        </div>
                    </div>
                </div>

                <div>
                    <div className="flex justify-between items-center mb-1 text-[12px]">
                        <div className="flex items-center gap-1 text-gray-800">
                            {StageIcon && <StageIcon className="w-3 h-3 text-emerald-600" />}
                            <span className="font-medium">{recordData?.pipeline_name}</span>
                        </div>
                        {recordData?.application_progress && (
                            <span className="text-slate-700">{recordData.application_progress}%</span>
                        )}
                    </div>

                    <div className="w-full h-2 rounded-full bg-slate-200 overflow-hidden">
                        <div
                            className={`h-full rounded-full pipeline-steps ${recordData?.pipeline_name?.toLowerCase().replace(/\s+/g, '-')}`}
                            style={{ width: `${recordData?.application_progress }%` }}
                        />
                    </div>
                </div>

                {recordData?.skills?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                        {recordData.skills.slice(0, 3).map((skill, i) => (
                            <Tag
                                key={i}
                                className="!text-[11px] !py-0.5 !px-2 !rounded-lg !bg-gray-50 !border-none !text-gray-800"
                            >
                                {skill}
                            </Tag>
                        ))}
                        {recordData.skills.length > 3 && (
                            <Tag className="!text-[11px] !py-0.5 !px-2 !rounded-lg !bg-gray-50 !border-none !text-gray-800">
                                +{skills.length - 3} more
                            </Tag>
                        )}
                    </div>
                )}

                {(!isClient && 
                    recordData?.applicant?.job_applications?.filter(
                    application => application.jap_id !== recordData?.jap_id
                ).length > 0) && (
                    <div className="w-full">
                        <div class="font-semibold">Other Pending Applications</div>
                        <div className="flex flex-wrap gap-2">
                        {recordData.applicant.job_applications.map((application, i) => (
                            <div className="flex gap-2 rounded-lg p-1 bg-gradient-to-br from-emerald-200 to-cyan-200">
                                <div className="p-1">{application.job_post.jpo_title}</div>
                                <div className={`pipeline-steps text-white rounded-lg p-1 px-2 ${application.pipeline_name?.toLowerCase().replace(/\s+/g, '-')}`}>{application.pipeline_name}</div>
                            </div>
                        ))}
                        </div>
                    </div>
                )}
            </div>
            <IndexModal
                open={showScoreDetails}
                onClose={() => setShowScoreDetails(false)}
                title={`${recordData?.applicant_name} Scores`}
                width={800}
            >
                <div>
                    <CandidateScoreCard scoreData={recordData?.applicant_score} isClientUser={authUser?.user_level === 4}/>
                </div>
            </IndexModal>
        </Card>
    );
};

const mapStateToProps = (state) => ({
    authUser: state.auth.user,
});

const mapDispatchToProps = (dispatch) => ({
    doApplicantScore: (data) => dispatch(getApplicantScore(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CandidateCard);
